// Der Argus-Stand aus dem Homelab-Protokoll (Topics argus.teil, argus.lauf,
// argus.gross; internal/argus/protokoll.go, docs/protokoll.md › „Argus über
// das Protokoll“) -- die reine Rechnung, ohne Runen und ohne Laufzeit-Importe,
// damit node (protokoll.test.mjs) sie laden kann. Den Store hält
// argus.svelte.ts.
//
//   argus.<feld>.stand        ein Feld der obersten Ebene von GET /v2-api/argus;
//                             laeufe: nur die Namen, in der Reihenfolge der Liste
//   argus.lauf.<lauf>.stand   ein Lauf aus laeufe
//   argus.<feld>.gross        schatten, kennzeichen, nachholen -- nur auf Abruf
//
// Jede Instanz trägt den Teil als JSON-Text im Feld `json`; `null` heißt: das
// Feld fehlt im Stand (der Dienst entfernt die Instanz nicht, sonst ginge das
// nach einer Unterbrechung verloren).

import type { Lauf, Stand } from "./typen";

/** Wohin eine Instanz gehört: Schlüssel im Stand („teil:<feld>“, „lauf:<lauf>“). */
export type Ort = { art: "teil"; feld: string; gross: boolean } | { art: "lauf"; lauf: string };

export function instanzOrt(inst: string): Ort | undefined {
  const t = inst.split(".");
  if (t[0] !== "argus") return undefined;
  if (t.length === 4 && t[1] === "lauf" && t[3] === "stand" && t[2]) return { art: "lauf", lauf: t[2] };
  if (t.length === 3 && t[1] && t[1] !== "zeit" && (t[2] === "stand" || t[2] === "gross")) {
    return { art: "teil", feld: t[1], gross: t[2] === "gross" };
  }
  return undefined;
}

export function ortSchluessel(o: Ort): string {
  return o.art === "lauf" ? `lauf:${o.lauf}` : `teil:${o.feld}`;
}

/** Der JSON-Text einer Instanz; undefined, wenn er fehlt oder nicht lesbar ist. */
export function liesJson(daten: unknown): { roh: string; wert: unknown } | undefined {
  const roh = (daten as { json?: unknown } | null)?.json;
  if (typeof roh !== "string") return undefined;
  try {
    return { roh, wert: JSON.parse(roh) };
  } catch {
    return undefined;
  }
}

/** Die Teile: Felder und Läufe, wie sie zuletzt kamen. `felder.laeufe` sind
 *  die Namen der Läufe in ihrer Reihenfolge. */
export interface Teile {
  felder: Map<string, unknown>;
  laeufe: Map<string, Lauf>;
}

/** Felder, die jeder Stand trägt (in internal/argus/modul.go `Stand` die ohne
 *  omitempty, außer `zeit`). Der Snapshot kommt je Instanz und über mehrere
 *  Rahmen verteilt: ein Stand entsteht erst, wenn alle da sind (auch als
 *  `null`) -- ein halber ließ die Leiste an `modelle.length` scheitern. */
export const PFLICHT: readonly string[] = [
  "bereit", "einstellungen", "schreibbar", "arbeitskopie", "klassen", "staende", "gesamt", "laeufe", "modelle",
  "hailo_vorgabe", "hailo_nacht", "kosten_monat", "laeufe_monat", "ablage", "werte", "uebernahme",
];

/** Der Stand aus den Teilen; undefined, solange `bereit` oder ein anderes
 *  Pflichtfeld fehlt. Die Läufe in der Reihenfolge von `laeufe`, nur die
 *  genannten. */
export function standAus(t: Teile, zeit: number): Stand | undefined {
  if (typeof t.felder.get("bereit") !== "boolean") return undefined;
  if (!PFLICHT.every((k) => t.felder.has(k))) return undefined;
  const s: Record<string, unknown> = Object.fromEntries([...t.felder].filter(([, v]) => v !== undefined));
  s.zeit = zeit;
  const namen = t.felder.get("laeufe");
  const reihe = Array.isArray(namen) ? (namen as string[]) : [...t.laeufe.keys()].sort((a, b) => (a < b ? 1 : a > b ? -1 : 0));
  s.laeufe = reihe.map((k) => t.laeufe.get(k)).filter((l): l is Lauf => !!l);
  return s as unknown as Stand;
}

/** Zerlegt einen Stand (Ablage) in seine Teile; er gilt als vollständig. */
export function teileAus(s: Stand): Teile {
  const felder = new Map<string, unknown>();
  for (const k of PFLICHT) felder.set(k, undefined);
  for (const [k, v] of Object.entries(s)) if (k !== "zeit" && k !== "laeufe") felder.set(k, v);
  const laeufe = new Map<string, Lauf>();
  for (const l of s.laeufe ?? []) laeufe.set(l.lauf, l);
  felder.set("laeufe", [...laeufe.keys()]);
  return { felder, laeufe };
}

/** Setzt einen Teil; `null` (Feld fehlt im Stand) merkt ihn als gekommen,
 *  aber leer -- der Stand trägt ihn dann nicht. */
export function setzeFeld(t: Teile, feld: string, wert: unknown): void {
  t.felder.set(feld, wert === null ? undefined : wert);
}
